import { useState, useEffect } from 'react';
import {
  Container,
  Stack,
  Group,
  Title,
  Text,
  Badge,
  Button,
  Card,
  SimpleGrid,
  Image,
  Anchor,
  ActionIcon,
  Skeleton,
  Center,
  Alert,
  Divider,
  Table,
  Tooltip,
  ScrollArea,
  Select,
  Switch,
} from '@mantine/core';
import {
  IconChevronLeft,
  IconAlertCircle,
  IconExternalLink,
  IconPhotoOff,
  IconRefresh,
  IconPencil,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useAppUser } from '../hooks/useAppUser';
import { usePageTitle } from '../hooks/usePageTitle';
import {
  fetchIngredient,
  refreshIngredient,
  fetchIngredientRecipes,
  updateIngredientClassification,
} from '../services/adminIngredientService';
import {
  IngredientRecord,
  IngredientProductTier,
  IngredientRecipes,
  RecipeUsingIngredient,
  RecipeUsingIngredientGroup,
  INGREDIENT_CATEGORY_LABELS,
  INGREDIENT_CATEGORY_ORDER,
  KROGER_BASE_URL,
} from '../types/ingredient';
import { PantryCategory } from '../types/pantry';

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString() : '—';

// Color-code the confidence badge: high = green, medium = yellow, low = red.
const confidenceColor = (confidence: number): string => {
  if (confidence >= 0.8) return 'green';
  if (confidence >= 0.5) return 'yellow';
  return 'red';
};

const TierCard = ({ tier, label }: { tier: IngredientProductTier; label: string }) => (
  <Card withBorder radius="md" padding="sm">
    <Stack gap="xs">
      <Group justify="space-between">
        <Badge variant="light" tt="capitalize">
          {label}
        </Badge>
        {tier.pricePerUnit !== undefined ? (
          <Text fw={700}>${tier.pricePerUnit.toFixed(2)}/unit</Text>
        ) : (
          <Tooltip label="No parseable per-unit price — excluded from cost math">
            <Badge color="red" variant="light" size="sm">
              no price
            </Badge>
          </Tooltip>
        )}
      </Group>

      {tier.imageUrl ? (
        tier.productPageURI ? (
          <Anchor
            href={`${KROGER_BASE_URL}${tier.productPageURI}`}
            target="_blank"
            rel="noreferrer"
            title="View on Kroger"
          >
            <Image
              src={tier.imageUrl}
              h={140}
              fit="contain"
              radius="sm"
              style={{ cursor: 'pointer' }}
            />
          </Anchor>
        ) : (
          <Image src={tier.imageUrl} h={140} fit="contain" radius="sm" />
        )
      ) : (
        <Center h={140}>
          <IconPhotoOff size={32} color="var(--mantine-color-gray-5)" />
        </Center>
      )}

      <Text size="sm" fw={500} lineClamp={2}>
        {tier.description}
      </Text>
      {tier.brand && (
        <Text size="xs" c="dimmed">
          {tier.brand}
        </Text>
      )}

      <Group gap="xs" justify="space-between">
        <Text size="xs" c="dimmed">
          {tier.size} · {tier.soldBy.toLowerCase()}
        </Text>
        <Text size="xs">${tier.price.toFixed(2)} pkg</Text>
      </Group>

      <Group gap={6} wrap="wrap">
        {tier.nutritionSource && (
          <Badge
            size="xs"
            variant="outline"
            color={tier.nutritionSource === 'kroger' ? 'blue' : 'grape'}
          >
            nutrition: {tier.nutritionSource.replace(/_/g, ' ')}
          </Badge>
        )}
        {tier.nutritionalRating !== undefined && (
          <Badge size="xs" variant="outline" color="gray">
            health {tier.nutritionalRating}
          </Badge>
        )}
        {tier.confidence !== undefined && (
          <Badge size="xs" variant="outline" color={confidenceColor(tier.confidence)}>
            conf {(tier.confidence * 100).toFixed(0)}%
          </Badge>
        )}
      </Group>

      {tier.reasoning && (
        <Text size="xs" c="dimmed" fs="italic" lineClamp={3}>
          {tier.reasoning}
        </Text>
      )}

      {tier.nutrition && (
        <>
          <Divider
            my={2}
            labelPosition="left"
            label={`Nutrition${
              tier.servingSize
                ? ` · per ${tier.servingSize.quantity} ${tier.servingSize.unit}`
                : ''
            }`}
          />
          <SimpleGrid cols={2} spacing="lg" verticalSpacing={2}>
            {(
              [
                ['Calories', `${tier.nutrition.calories}`],
                ['Protein', `${tier.nutrition.protein} g`],
                ['Carbs', `${tier.nutrition.carbohydrates} g`],
                ['Fat', `${tier.nutrition.fat} g`],
                ['Fiber', `${tier.nutrition.fiber} g`],
                ['Sugar', `${tier.nutrition.sugar} g`],
                ['Sat fat', `${tier.nutrition.saturatedFat} g`],
                ['Trans fat', `${tier.nutrition.transFat} g`],
                ['Sodium', `${tier.nutrition.sodium} mg`],
                ['Cholesterol', `${tier.nutrition.cholesterol} mg`],
              ] as [string, string][]
            ).map(([label, value]) => (
              <Group key={label} justify="space-between" gap={4} wrap="nowrap">
                <Text size="xs" c="dimmed">
                  {label}
                </Text>
                <Text size="xs" fw={500}>
                  {value}
                </Text>
              </Group>
            ))}
          </SimpleGrid>
        </>
      )}

      {tier.productPageURI && (
        <Anchor
          href={`${KROGER_BASE_URL}${tier.productPageURI}`}
          target="_blank"
          rel="noreferrer"
          size="xs"
        >
          <Group gap={4}>
            <IconExternalLink size={12} /> View on Kroger
          </Group>
        </Anchor>
      )}
    </Stack>
  </Card>
);

const SkeletonTierCard = () => (
  <Card withBorder radius="md" padding="sm">
    <Stack gap="xs">
      <Group justify="space-between">
        <Skeleton height={20} width={64} radius="sm" />
        <Skeleton height={20} width={72} radius="sm" />
      </Group>
      <Skeleton height={140} radius="sm" />
      {/* description (lineClamp 2) */}
      <Skeleton height={14} width="90%" />
      <Skeleton height={14} width="65%" />
      {/* brand */}
      <Skeleton height={10} width="40%" />
      {/* size · soldBy + pkg price */}
      <Group justify="space-between">
        <Skeleton height={10} width="45%" />
        <Skeleton height={10} width="25%" />
      </Group>
      {/* source / rating / confidence badges */}
      <Group gap={6}>
        <Skeleton height={16} width={84} radius="xl" />
        <Skeleton height={16} width={64} radius="xl" />
        <Skeleton height={16} width={56} radius="xl" />
      </Group>
      {/* reasoning (lineClamp 3) */}
      <Skeleton height={10} width="100%" />
      <Skeleton height={10} width="100%" />
      <Skeleton height={10} width="55%" />
      {/* nutrition divider label */}
      <Skeleton height={10} width={120} my={2} />
      <SimpleGrid cols={2} spacing="lg" verticalSpacing={6}>
        {Array.from({ length: 10 }).map((_, i) => (
          <Group key={i} justify="space-between" gap={4} wrap="nowrap">
            <Skeleton height={10} width="50%" />
            <Skeleton height={10} width="25%" />
          </Group>
        ))}
      </SimpleGrid>
      {/* Kroger link */}
      <Skeleton height={10} width={100} />
    </Stack>
  </Card>
);

const DetailSkeleton = () => (
  <>
    <Group gap="xs">
      <Skeleton height={22} width={90} radius="xl" />
      <Skeleton height={22} width={70} radius="xl" />
    </Group>
    <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
      {Array.from({ length: 3 }).map((_, i) => (
        <SkeletonTierCard key={i} />
      ))}
    </SimpleGrid>
    <Divider label="Record" labelPosition="left" />
    <Stack gap="xs">
      {Array.from({ length: 4 }).map((_, i) => (
        <Group key={i} justify="space-between" wrap="nowrap">
          <Skeleton height={12} width="30%" />
          <Skeleton height={12} width="45%" />
        </Group>
      ))}
    </Stack>
  </>
);

// One recipe row in the "recipes using this ingredient" section. Catalog and
// user recipes link to their respective detail routes; a user recipe missing
// its owner ID (shouldn't happen) renders as plain text rather than a dead link.
const RecipeRow = ({ recipe }: { recipe: RecipeUsingIngredient }) => {
  const href =
    recipe.type === 'catalog'
      ? `/admin/catalog-recipe/${recipe.recipeId}`
      : recipe.userId
        ? `/u/${recipe.userId}/recipe/${recipe.recipeId}`
        : undefined;

  const label = (
    <Group gap="xs" wrap="nowrap">
      <Text size="sm" lineClamp={1}>
        {recipe.name}
      </Text>
      {recipe.isCurated && (
        <Badge size="xs" color="yellow" variant="light">
          top pick
        </Badge>
      )}
    </Group>
  );

  return (
    <Group justify="space-between" wrap="nowrap" gap="xs">
      {href ? (
        <Anchor component={Link} to={href} underline="hover">
          {label}
        </Anchor>
      ) : (
        label
      )}
      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
        {recipe.servings} {recipe.servings === 1 ? 'serving' : 'servings'}
      </Text>
    </Group>
  );
};

// One labelled group (catalog / user) of recipes that use the ingredient. The
// header shows the true total; the list is capped server-side, so when it's
// truncated we render it in a bounded, scrollable box and note "first N of M"
// rather than letting a common ingredient flood the page.
const RecipeGroupSection = ({
  title,
  color,
  group,
  rowKey,
}: {
  title: string;
  color: string;
  group: RecipeUsingIngredientGroup;
  rowKey: (r: RecipeUsingIngredient) => string;
}) => {
  if (group.total === 0) return null;
  const truncated = group.total > group.recipes.length;

  return (
    <Stack gap="xs">
      <Group gap="xs">
        <Text size="sm" fw={600}>
          {title}
        </Text>
        <Badge size="xs" variant="light" color={color}>
          {group.total}
        </Badge>
      </Group>
      <ScrollArea.Autosize mah={260} type="auto" offsetScrollbars>
        <Stack gap="xs" pr={truncated ? 'sm' : undefined}>
          {group.recipes.map((r) => (
            <RecipeRow key={rowKey(r)} recipe={r} />
          ))}
        </Stack>
      </ScrollArea.Autosize>
      {truncated && (
        <Text size="xs" c="dimmed">
          Showing first {group.recipes.length} of {group.total}.
        </Text>
      )}
    </Stack>
  );
};

export const IngredientDetailPage = () => {
  const { name = '' } = useParams();
  usePageTitle(name ? `${name} | Meals You Love` : 'Ingredient | Meals You Love');
  const { user, loading: userLoading } = useAppUser();
  const navigate = useNavigate();
  const isAdmin = !!(user?.isAdmin || user?._adminOverride);

  const [record, setRecord] = useState<IngredientRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [recipes, setRecipes] = useState<IngredientRecipes | null>(null);
  const [recipesLoading, setRecipesLoading] = useState(true);
  const [editingClass, setEditingClass] = useState(false);
  const [editCategory, setEditCategory] = useState<PantryCategory | undefined>(undefined);
  const [editStaple, setEditStaple] = useState(false);
  const [savingClass, setSavingClass] = useState(false);

  const beginEditClass = () => {
    setEditCategory(record?.category);
    setEditStaple(record?.isStaple ?? false);
    setEditingClass(true);
  };

  const saveClassification = async () => {
    if (!user || !record) return;
    setSavingClass(true);
    try {
      const updated = await updateIngredientClassification(user, name, {
        ...(editCategory !== undefined ? { category: editCategory } : {}),
        isStaple: editStaple,
      });
      setRecord(updated);
      setEditingClass(false);
      notifications.show({
        title: 'Updated',
        message: 'Classification saved',
        color: 'teal',
      });
    } catch (e: any) {
      notifications.show({
        title: 'Save failed',
        message: e.message || 'Failed to save classification',
        color: 'red',
      });
    } finally {
      setSavingClass(false);
    }
  };

  const handleRefresh = async () => {
    if (!user) return;
    setRefreshing(true);
    try {
      const updated = await refreshIngredient(user, name);
      setRecord(updated);
      notifications.show({
        title: 'Refreshed',
        message: `"${name}" re-fetched from Kroger`,
        color: 'teal',
      });
    } catch (e: any) {
      notifications.show({
        title: 'Refresh failed',
        message: e.message || 'Failed to refresh ingredient',
        color: 'red',
      });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (!userLoading && user && !isAdmin) navigate('/');
  }, [userLoading, user, isAdmin, navigate]);

  useEffect(() => {
    if (!user || !isAdmin) return;
    let cancelled = false;
    setLoading(true);
    fetchIngredient(user, name)
      .then((r) => !cancelled && (setRecord(r), setError(null)))
      .catch((e) => !cancelled && setError(e.message || 'Failed to load'))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, name]);

  // Recipes using this ingredient load independently of the record itself.
  useEffect(() => {
    if (!user || !isAdmin) return;
    let cancelled = false;
    setRecipesLoading(true);
    fetchIngredientRecipes(user, name)
      .then((r) => !cancelled && setRecipes(r))
      .catch(() => !cancelled && setRecipes(null))
      .finally(() => !cancelled && setRecipesLoading(false));
    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, name]);

  const tiers: Array<['value' | 'mid' | 'premium', IngredientProductTier]> = record
    ? (['value', 'mid', 'premium'] as const)
        .map((k) => [k, record.productTiers[k]] as const)
        .filter((e): e is ['value' | 'mid' | 'premium', IngredientProductTier] => !!e[1])
    : [];

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group wrap="nowrap" justify="space-between">
          <Group wrap="nowrap" gap="xs">
            <ActionIcon variant="subtle" size="lg" onClick={() => navigate('/admin/ingredients')}>
              <IconChevronLeft size={20} />
            </ActionIcon>
            <Stack gap={0}>
              {loading ? (
                <Skeleton height={30} width={220} />
              ) : (
                <>
                  <Title order={2}>{record?.displayNames?.[0] ?? name}</Title>
                  {record?.displayNames?.[0] &&
                    record.displayNames[0].toLowerCase() !== name.toLowerCase() && (
                      <Text size="xs" c="dimmed">
                        key: {name}
                      </Text>
                    )}
                </>
              )}
            </Stack>
          </Group>
          <Button
            leftSection={<IconRefresh size={16} />}
            variant="light"
            loading={refreshing}
            disabled={loading}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </Group>

        {error && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {error}
          </Alert>
        )}

        {loading ? (
          <DetailSkeleton />
        ) : record ? (
          <>
            <Group gap="xs">
              {record.notAnIngredient && (
                <Badge color="gray" variant="light">
                  not an ingredient
                </Badge>
              )}
              {tiers.length === 0 && !record.notAnIngredient && (
                <Badge color="red" variant="light">
                  unresolved
                </Badge>
              )}
              {record.lastRefreshDegradedAt && (
                <Tooltip label={`Holding stale data since ${fmtDate(record.lastRefreshDegradedAt)}`}>
                  <Badge color="orange" variant="light">
                    degraded
                  </Badge>
                </Tooltip>
              )}
              {record.defaultUnit && (
                <Badge variant="outline">unit: {record.defaultUnit}</Badge>
              )}
            </Group>

            {record.displayNames && record.displayNames.length > 1 && (
              <Group gap="xs" align="center">
                <Text size="xs" c="dimmed">
                  Also called:
                </Text>
                {record.displayNames.slice(1).map((alias) => (
                  <Badge key={alias} size="sm" variant="light" color="gray">
                    {alias}
                  </Badge>
                ))}
              </Group>
            )}

            <Card withBorder radius="md" padding="sm">
              <Group justify="space-between" wrap="nowrap">
                <Text size="sm" fw={600}>
                  Classification
                </Text>
                {!editingClass ? (
                  <Button
                    size="compact-sm"
                    variant="subtle"
                    leftSection={<IconPencil size={14} />}
                    onClick={beginEditClass}
                  >
                    Edit
                  </Button>
                ) : (
                  <Group gap="xs" wrap="nowrap">
                    <Button
                      size="compact-sm"
                      variant="subtle"
                      color="gray"
                      onClick={() => setEditingClass(false)}
                      disabled={savingClass}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="compact-sm"
                      loading={savingClass}
                      onClick={saveClassification}
                    >
                      Save
                    </Button>
                  </Group>
                )}
              </Group>

              {editingClass ? (
                <Group mt="sm" gap="lg" align="flex-end">
                  <Select
                    label="Category"
                    w={220}
                    placeholder="Select category"
                    value={editCategory ?? null}
                    onChange={(v) =>
                      setEditCategory((v as PantryCategory) || undefined)
                    }
                    data={INGREDIENT_CATEGORY_ORDER.map((c) => ({
                      value: c,
                      label: INGREDIENT_CATEGORY_LABELS[c],
                    }))}
                  />
                  <Switch
                    label="Staple"
                    checked={editStaple}
                    onChange={(e) => setEditStaple(e.currentTarget.checked)}
                    mb={6}
                  />
                </Group>
              ) : (
                <Group mt="xs" gap="xs" align="center">
                  <Badge color="blue" variant="light">
                    {record.category
                      ? INGREDIENT_CATEGORY_LABELS[record.category]
                      : 'uncategorized'}
                  </Badge>
                  {record.isStaple && (
                    <Badge color="grape" variant="light">
                      staple
                    </Badge>
                  )}
                  {record.classificationSource && (
                    <Text size="xs" c="dimmed">
                      {record.classificationSource === 'curated'
                        ? 'curated'
                        : record.classificationSource === 'admin'
                          ? 'admin override'
                          : 'AI-classified'}
                    </Text>
                  )}
                </Group>
              )}
            </Card>

            {record.notAnIngredientReason && (
              <Alert color="gray" variant="light" icon={<IconAlertCircle size={16} />}>
                Flagged not-an-ingredient: {record.notAnIngredientReason}
              </Alert>
            )}

            {tiers.length > 0 ? (
              <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
                {tiers.map(([k, t]) => (
                  <TierCard key={k} tier={t} label={k} />
                ))}
              </SimpleGrid>
            ) : (
              !record.notAnIngredient && (
                <Text c="dimmed" size="sm">
                  No product tiers — Kroger returned no usable products for this ingredient.
                </Text>
              )
            )}

            <Divider label="Record" labelPosition="left" />
            <Table withTableBorder>
              <Table.Tbody>
                <Table.Tr>
                  <Table.Td w={220}><Text size="sm" c="dimmed">Location</Text></Table.Td>
                  <Table.Td><Text size="sm">{record.locationId}</Text></Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text size="sm" c="dimmed">Last Kroger sync (attempt)</Text></Table.Td>
                  <Table.Td><Text size="sm">{fmtDate(record.lastKrogerSync)}</Text></Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td><Text size="sm" c="dimmed">Last successful refresh</Text></Table.Td>
                  <Table.Td><Text size="sm">{fmtDate(record.lastSuccessfulKrogerSync)}</Text></Table.Td>
                </Table.Tr>
                {record.lastRefreshDegradedAt && (
                  <Table.Tr>
                    <Table.Td><Text size="sm" c="dimmed">Degraded since</Text></Table.Td>
                    <Table.Td><Text size="sm" c="orange">{fmtDate(record.lastRefreshDegradedAt)}</Text></Table.Td>
                  </Table.Tr>
                )}
                <Table.Tr>
                  <Table.Td><Text size="sm" c="dimmed">Created</Text></Table.Td>
                  <Table.Td><Text size="sm">{fmtDate(record.createdAt)}</Text></Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>

            {record.unitConversions && record.unitConversions.length > 0 && (
              <>
                <Divider label="Unit conversions" labelPosition="left" />
                <Table withTableBorder verticalSpacing="xs">
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>From</Table.Th>
                      <Table.Th>To</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>Factor</Table.Th>
                      <Table.Th>Prep</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {record.unitConversions.map((c, i) => (
                      <Table.Tr key={i}>
                        <Table.Td><Text size="sm">{c.fromUnit}</Text></Table.Td>
                        <Table.Td><Text size="sm">{c.toUnit}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}>
                          <Text size="sm">{c.factor}</Text>
                        </Table.Td>
                        <Table.Td><Text size="sm" c="dimmed">{c.preparation ?? '—'}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </>
            )}
          </>
        ) : null}

        {!loading && (
          <>
            <Divider label="Recipes using this ingredient" labelPosition="left" />
            {recipesLoading ? (
              <Stack gap="xs">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} height={16} width="70%" />
                ))}
              </Stack>
            ) : recipes && (recipes.catalog.total || recipes.user.total) ? (
              <Stack gap="lg">
                <RecipeGroupSection
                  title="Catalog"
                  color="blue"
                  group={recipes.catalog}
                  rowKey={(r) => r.recipeId}
                />
                <RecipeGroupSection
                  title="User recipes"
                  color="grape"
                  group={recipes.user}
                  rowKey={(r) => `${r.userId}/${r.recipeId}`}
                />
              </Stack>
            ) : (
              <Text c="dimmed" size="sm">
                No recipes reference this ingredient yet. Recipes created before
                this index was added won't appear until the
                normalized-ingredient-names backfill has run.
              </Text>
            )}
          </>
        )}
      </Stack>
    </Container>
  );
};
